import React, { useEffect, useState, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Link, useParams, useRouteMatch } from "react-router-dom";
import MaterialTable from "material-table";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  TextField,
  InputLabel,
} from "@material-ui/core";
import ClearIcon from "@material-ui/icons/HighlightOff";
import AddIcon from "@material-ui/icons/Add";
import ImageLoader from "react-load-image";

import {
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  ROLES,
} from "../../../../utilities/constants";
import {
  getAdminsForOrgRequest,
  removeAdminFromOrgRequest,
} from "../../AdministratorsApiActions";
import ConfirmationDialog from "../../../../utilities/components/ConfirmationDialog/ConfirmationDialog";
import { useStyles } from "./styles";
import defaultUser from "../../../../assets/user.png";
import { getUserInfo } from "../../../../utilities/storage";
import { debounce } from "../../../../utilities/utilityFunctions";
import Preloader from "../../../../utilities/components/Preloader/Preloader";
import ActiveStatusIcon from "../../../../utilities/components/ActiveStatusIcon/ActiveStatusIcon";
import { MTableToolbar } from "material-table";

/**
 * function to get message for remove dialog
 * @param admin: admin details
 * @returns {JSX.Element}
 * @constructor
 */
const RemoveDialogMessage = ({ admin, classes }) => {
  return (
    <>
      {admin && (
        <Box>
          Are you sure you want to remove the{" "}
          <span className={classes.dialogMessageLabel}>
            {admin.firstName} {admin.lastName}
          </span>{" "}
          from admin list?
        </Box>
      )}
    </>
  );
};

/**
 * function to render department admins list
 * @param getAdminsForOrg: function to get admins of organization
 * @returns {JSX.Element}
 * @constructor
 */
const Administrators = ({
  history,
  getAdminsForOrg,
  admins,
  removeAdminFromOrg,
  orgIdHistory,
  orgNameHistory,
}) => {
  const classes = useStyles();
  const params = useParams();
  const match = useRouteMatch();
  const role = getUserInfo().Roles[0].name;

  const [localFilters, setLocalFilters] = useState({
    name: "",
    email: "",
    phone: "",
    active: "",
  });

  const [apiParams, setApiParams] = useState({
    page: 0,
    rowsPerPage: PAGE_SIZE,
    searchData: { name: "", email: "", phone: "", active: "" },
    isHideLoader: false,
  });

  const [openRemoveConfirmationDialog, setOpenRemoveConfirmationDialog] =
    useState(false);
  const [admin, setAdmin] = useState(null);

  const updateApiParams = useCallback(
    debounce((filters) => {
      setApiParams((prev) => ({
        ...prev,
        searchData: filters,
        page: 0,
      }));
    }, 400),
    []
  );

  const handleFilterChange = (field) => (e) => {
    const value = e.target.value;
    const updatedFilters = { ...localFilters, [field]: value };
    setLocalFilters(updatedFilters);
    updateApiParams(updatedFilters);
  };

  const handleSelectChange = (field) => (e) => {
    const value = e.target.value;
    const updatedFilters = { ...localFilters, [field]: value };
    setLocalFilters(updatedFilters);
    setApiParams((prev) => ({
      ...prev,
      searchData: updatedFilters,
      page: 0,
    }));
  };

  useEffect(() => {
    getAdminsForOrg(params.id, apiParams, true);
  }, [params.id, apiParams, getAdminsForOrg]);

  /**
   * function to remove admin on button click
   */
  const onRemove = () => {
    const data = { adminId: admin.userId };
    removeAdminFromOrg(params.id, data).then(() => {
      setOpenRemoveConfirmationDialog(false);
      getAdminsForOrg(params.id, apiParams);
    });
  };

  /** Navigate to profile */
  const handleProfileRoute = (userId) => {
    if (role === ROLES.ROLE_SYSTEM_ADMIN) {
      history.push({
        pathname: `${match.path.split(":")[0]}admins/${userId}`,
        state: { orgIdHistory, orgNameHistory },
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        title: "Name",
        filtering: false,
        cellStyle: { width: "25%" },
        render: ({ imageUrl, name, userId }) => (
          <Box
            className={`${classes.profileContainer} ${
              role === ROLES.ROLE_SYSTEM_ADMIN ? classes.cursorPointer : ""
            }`}
            onClick={() => handleProfileRoute(userId)}
          >
            <Box className={classes.profileInnerContainer}>
              <ImageLoader src={imageUrl || defaultUser}>
                <img alt={userId} className={classes.profileImage} />
                <div className={classes.profileImageError}>{userId}</div>
                <Preloader alt={userId} className={classes.profileImage} />
              </ImageLoader>
            </Box>
            <Box className={classes.nameContainer}>
              <Typography classes={{ root: classes.name }}>{name}</Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: "email",
        title: "Email",
        filtering: false,
        cellStyle: { width: "25%" },
      },
      {
        field: "phone",
        title: "Phone",
        filtering: false,
        cellStyle: { width: "25%" },
      },
      {
        field: "enabled",
        title: "Active Status",
        filtering: false,
        cellStyle: { width: "12%", textAlign: "center" },
        render: ({ enabled, isBanned }) => (
          <ActiveStatusIcon
            enabled={enabled}
            isBanned={isBanned}
            isLabel={false}
          />
        ),
      },
      ...(role === ROLES.ROLE_SYSTEM_ADMIN
        ? [
            {
              field: "delete",
              title: "Remove",
              filtering: false,
              sorting: false,
              cellStyle: { width: "10%", textAlign: "center" },
              render: (props) => (
                <Tooltip arrow placement="top" title="Remove">
                  <ClearIcon
                    className={classes.removeIcon}
                    onClick={() => {
                      setOpenRemoveConfirmationDialog(true);
                      setAdmin(props);
                    }}
                  />
                </Tooltip>
              ),
            },
          ]
        : []),
    ],
    [classes, role]
  );

  const FilterComponent = useMemo(() => {
    const marginRightValue = role === ROLES.ROLE_CLINIC_OWNER ? 70 : 90;
    return (
      <Box
        display="flex"
        gap={3}
        sx={{
          padding: "0 16px",
          flexWrap: "wrap",
          "& .MuiFormControl-root, & .MuiTextField-root": {
            marginRight: marginRightValue,
          },
        }}
      >
        <TextField
          label="Search Name"
          variant="outlined"
          size="small"
          value={localFilters.name}
          onChange={handleFilterChange("name")}
        />
        <TextField
          label="Search Email"
          variant="outlined"
          size="small"
          value={localFilters.email}
          onChange={handleFilterChange("email")}
        />
        <TextField
          label="Search Phone"
          variant="outlined"
          size="small"
          value={localFilters.phone}
          onChange={handleFilterChange("phone")}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={localFilters.active}
            onChange={handleSelectChange("active")}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Blocked</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  }, [localFilters]);

  if (role === ROLES.ROLE_SYSTEM_ADMIN) {
    columns.push({
      field: "delete",
      title: "Remove",
      filtering: false,
      sorting: false,
      cellStyle: { width: "10%", textAlign: "center" },
      headerStyle: { width: "10%", textAlign: "center" },
      render: (props) => (
        <Tooltip arrow placement="top" title="Remove">
          <ClearIcon
            className={classes.removeIcon}
            onClick={() => {
              setOpenRemoveConfirmationDialog(true);
              setAdmin(props);
            }}
          />
        </Tooltip>
      ),
    });
  }

  return (
    <Box>
      {(role === ROLES.ROLE_SYSTEM_ADMIN ||
        role === ROLES.ROLE_CLINIC_OWNER) && (
        <Box className={classes.addButtonContainer}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            disableElevation
            component={Link}
            to={{
              pathname: `${match.path.split(":")[0]}addAdmin`,
              state: {
                orgIdHistory: orgIdHistory,
                orgNameHistory: orgNameHistory,
              },
            }}
            classes={{ root: classes.addButton }}
          >
            Administrator
          </Button>
        </Box>
      )}

      <Box>
        {FilterComponent}

        <MaterialTable
          title="Administrators"
          columns={columns}
          data={admins.rows}
          style={{ marginTop: "20px" }}
          options={{
            search: false,
            filtering: false,
            sorting: true,
            draggable: false,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            pageSize: apiParams.rowsPerPage,
          }}
          totalCount={admins.count}
          page={apiParams.page}
          onChangeRowsPerPage={(pageSize) =>
            setApiParams((prev) => ({ ...prev, rowsPerPage: pageSize }))
          }
          onChangePage={(page) => setApiParams((prev) => ({ ...prev, page }))}
          components={{
            Toolbar: (props) => <MTableToolbar {...props} />,
          }}
        />
      </Box>

      {openRemoveConfirmationDialog && admin && (
        <ConfirmationDialog
          title="Remove"
          message={<RemoveDialogMessage admin={admin} classes={classes} />}
          open={openRemoveConfirmationDialog}
          setOpen={setOpenRemoveConfirmationDialog}
          onConfirm={onRemove}
        />
      )}
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    admins: state.administrators.admins,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAdminsForOrg: (orgId, data, isHideLoader = false) => {
      return dispatch(getAdminsForOrgRequest(orgId, data, isHideLoader)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    removeAdminFromOrg: (orgId, data) => {
      return dispatch(removeAdminFromOrgRequest(orgId, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Administrators);
